import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  Container,
  Slide,
  Zoom,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Autocomplete,
  Menu,
  Fab,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery as useMediaQueryMui
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  SwapHoriz as SwapHorizIcon,
  AccountBalanceWallet as WalletIcon,
  Send as SendIcon,
  ArrowDownward as WithdrawIcon,
  Refresh as RefreshIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Menu as MenuIcon,
  ExitToApp as ExitToAppIcon,
  History as HistoryIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Add as AddIcon,
  AccountCircle as AccountCircleIcon,
  Euro as EuroIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { logoutUser } from '@/store/slices/authSlice';
import { getBalances, getTransactions, getCreditCards } from '@/store/slices/userSlice';
import api from '@/services/authService';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ApplyForFundingModal from '@/components/ApplyForFundingModal';

interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  defaultCurrency?: string;
}

interface Balance {
  currency: string;
  balance: number;
}

interface Transaction {
  id: number;
  userId: number;
  transactionType: 'deposit' | 'withdrawal' | 'transfer';
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  description: string;
  recipientUsername?: string;
  createdAt: string;
}

interface CreditCard {
  id: number;
  cardType: 'VISA' | 'MASTER';
  cardLastFour: string;
  cardNumber?: string;
  cardholderName?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cvv?: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const FundingIcon = () => <AccountBalanceIcon />;

// Main UserDashboard component
// This component serves as the primary user interface for banking operations
// including balance viewing, transfers, withdrawals, and profile management
const UserDashboardModern: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQueryMui(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQueryMui(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { balances, transactions, creditCards, isLoading } = useSelector((state: RootState) => state.user);

  const [darkMode, setDarkMode] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [transferType, setTransferType] = useState<'username' | 'account' | 'card'>('username');
  const [transferForm, setTransferForm] = useState({
    recipientUsername: '',
    amount: '',
    currency: 'EUR' as 'EUR' | 'USD',
    description: '',
  });
  const [accountTransferForm, setAccountTransferForm] = useState({
    beneficiary: '',
    accountNumber: '',
    bankName: '',
    swiftBic: '',
    country: '',
    phoneNumber: '',
    address: '',
    amount: '',
    currency: 'EUR' as 'EUR' | 'USD',
    description: '',
  });
  const [cardTransferForm, setCardTransferForm] = useState({
    currency: 'EUR' as 'EUR' | 'USD',
    cardType: 'VISA' as 'VISA' | 'MASTER',
    creditCardId: '',
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    amount: '',
    description: '',
  });
  const [withdrawMethod, setWithdrawMethod] = useState<'card' | 'bank'>('card');
  const [withdrawForm, setWithdrawForm] = useState({
    currency: 'EUR' as 'EUR' | 'USD',
    cardType: 'VISA' as 'VISA' | 'MASTER',
    creditCardId: '',
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    beneficiary: '',
    accountNumber: '',
    bankName: '',
    swiftBic: '',
    country: '',
    phoneNumber: '',
    address: '',
    amount: '',
    description: '',
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [transferMenuAnchor, setTransferMenuAnchor] = useState<null | HTMLElement>(null);
  const [profileForm, setProfileForm] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    defaultCurrency: user?.defaultCurrency || 'EUR' as 'EUR' | 'USD',
  });

  // Initialize profile form when user data changes
  useEffect(() => {
    if (user) {
      setProfileForm({
        fullName: user.fullName || '',
        email: user.email || '',
        phone: '',
        address: {
          street: user.address?.street || '',
          city: user.address?.city || '',
          state: user.address?.state || '',
          zipCode: user.address?.zipCode || '',
          country: user.address?.country || '',
        },
        defaultCurrency: user.defaultCurrency || 'EUR' as 'EUR' | 'USD',
      });
    }
  }, [user]);

  const menuItems: MenuItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'transactions', label: 'Transactions', icon: <HistoryIcon /> },
    { id: 'transfer', label: 'Transfer', icon: <SwapHorizIcon /> },
    { id: 'withdraw', label: 'Withdraw', icon: <WithdrawIcon /> },
    { id: 'profile', label: 'Profile', icon: <AccountCircleIcon /> },
  ];

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const fetchUserData = async () => {
    console.log('Fetching user data...');
    try {
      // Fetch all data in parallel but handle errors individually
      const results = await Promise.allSettled([
        dispatch(getBalances()),
        dispatch(getTransactions({ page: 1, limit: 10 })),
        dispatch(getCreditCards()),
      ]);
      
      // Check if any requests failed
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.warn('Some data fetches failed:', failures);
        // Set a warning but don't block the dashboard
        setError('Some data could not be loaded. Please refresh the page.');
      } else {
        console.log('User data fetched successfully');
        setError(null);
      }
    } catch (err: any) {
      console.error('Error fetching user data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch user data');
    } finally {
      setIsInitializing(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setUserOptions([]);
      return;
    }
    try {
      setSearchLoading(true);
      const response = await api.get(`/user/users/search?q=${query}`);
      setUserOptions(response.data.data.users);
    } catch (err: any) {
      console.error('Error searching users:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Redirect directly to error page
    window.location.href = '/transfer-error';
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Redirect directly to error page
    window.location.href = '/withdraw-error';
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  const handleUpdateProfile = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('accessToken');
      // Only send fields that the backend accepts
      const updateData = {
        fullName: profileForm.fullName,
        address: profileForm.address,
        defaultCurrency: profileForm.defaultCurrency,
      };
      await api.put('/user/profile', updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Profile updated successfully!');
      setProfileDialogOpen(false);
      await fetchUserData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const renderDashboardStats = () => {
    console.log('Rendering dashboard stats, balances:', balances);
    
    // Check if balances is valid
    if (!balances || !Array.isArray(balances)) {
      return (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12}>
            <Alert severity="info">
              Loading balances...
            </Alert>
          </Grid>
        </Grid>
      );
    }
    
    return (
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12} sm={6}>
            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
              <Card sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
              }}>
            <Box sx={{
              position: 'absolute',
              top: '-40%',
              right: '-40%',
              width: '80%',
              height: '80%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            }} />
            <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    EUR Balance
                  </Typography>
                  <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                    €{Array.isArray(balances) && (() => {
                      const balance = parseFloat(String(balances.find(b => b.currency === 'EUR')?.balance ?? '0'));
                      return isNaN(balance) ? '0.00' : balance.toFixed(2);
                    })()}
                  </Typography>
                </Box>
                <Avatar sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(12px)',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                }}>
                  <EuroIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                </Avatar>
              </Box>
              <Chip
                label="Euro"
                size="small"
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(12px)',
                }}
              />
            </CardContent>
          </Card>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
          }}>
            <Box sx={{
              position: 'absolute',
              top: '-40%',
              right: '-40%',
              width: '80%',
              height: '80%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            }} />
            <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    USD Balance
                  </Typography>
                  <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                    ${Array.isArray(balances) && (() => {
                      const balance = parseFloat(String(balances.find(b => b.currency === 'USD')?.balance ?? '0'));
                      return isNaN(balance) ? '0.00' : balance.toFixed(2);
                    })()}
                  </Typography>
                </Box>
                <Avatar sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(12px)',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                }}>
                  <AttachMoneyIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                </Avatar>
              </Box>
              <Chip
                label="US Dollar"
                size="small"
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(12px)',
                }}
              />
            </CardContent>
          </Card>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(217, 119, 6, 0.3)',
          }}>
            <Box sx={{
              position: 'absolute',
              top: '-40%',
              right: '-40%',
              width: '80%',
              height: '80%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            }} />
            <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Transactions
                  </Typography>
                  <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                    {transactions?.length || 0}
                  </Typography>
                </Box>
                <Avatar sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(12px)',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                }}>
                  <HistoryIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                </Avatar>
              </Box>
              <Chip
                label="History"
                size="small"
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(12px)',
                }}
              />
            </CardContent>
          </Card>
        </Zoom>
      </Grid>
    </Grid>
  );
};

  const renderBalanceTrends = () => (
    <Card sx={{
      background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
      borderRadius: { xs: 12, sm: 16 },
    }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar sx={{
            background: 'rgba(99, 102, 241, 0.15)',
            width: 40,
            height: 40,
          }}>
            <WalletIcon sx={{ color: '#6366f1', fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" fontWeight="600">Balance Overview</Typography>
        </Box>
        <Grid container spacing={2}>
          {balances && balances.length > 0 ? balances.map((balance) => (
            <Grid item xs={12} sm={6} key={balance.currency}>
              <Box sx={{
                p: 2,
                borderRadius: { xs: 8, sm: 12 },
                background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                  {balance.currency} Balance
                </Typography>
                <Typography variant="h6" fontWeight="700">
                  {formatCurrency(balance.balance, balance.currency)}
                </Typography>
              </Box>
            </Grid>
          )) : (
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: { xs: 8, sm: 12 }, background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                <Typography variant="body2" color="textSecondary">No balances available</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderRecentTransactions = () => (
    <Card sx={{
      background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
      borderRadius: { xs: 12, sm: 16 },
    }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{
              background: 'rgba(99, 102, 241, 0.15)',
              width: 40,
              height: 40,
            }}>
              <HistoryIcon sx={{ color: '#6366f1', fontSize: 20 }} />
            </Avatar>
            <Typography variant="h6" fontWeight="600">Recent Transactions</Typography>
          </Box>
          <IconButton onClick={fetchUserData} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          {transactions?.slice(0, 5).map((transaction, index) => (
            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={transaction.id}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderRadius: { xs: 8, sm: 12 },
                background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                transition: 'all 0.2s',
                '&:hover': {
                  background: darkMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.05)',
                },
              }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{
                    background: transaction.transactionType === 'deposit' ? 'rgba(16, 185, 129, 0.15)' :
                                   transaction.transactionType === 'withdrawal' ? 'rgba(239, 68, 68, 0.15)' :
                                   'rgba(139, 92, 246, 0.15)',
                    width: 36,
                    height: 36,
                  }}>
                    {transaction.transactionType === 'deposit' ? <WalletIcon sx={{ color: '#10b981', fontSize: 18 }} /> :
                     transaction.transactionType === 'withdrawal' ? <WithdrawIcon sx={{ color: '#ef4444', fontSize: 18 }} /> :
                     <SwapHorizIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                      {transaction.transactionType ?
                        transaction.transactionType.charAt(0).toUpperCase() + transaction.transactionType.slice(1) :
                        'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      {new Date(transaction.createdAt).toLocaleDateString()} • {transaction.description || 'No description'}
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body1" fontWeight="700" sx={{ fontSize: '0.9rem' }}>
                    {transaction.transactionType === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount, transaction.currency)}
                  </Typography>
                  <Chip
                    label={transaction.status}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      borderRadius: 4,
                      background:
                        transaction.status === 'completed'
                          ? 'rgba(16, 185, 129, 0.15)'
                          : transaction.status === 'pending'
                            ? 'rgba(245, 158, 11, 0.15)'
                            : 'rgba(239, 68, 68, 0.15)',
                      color:
                        transaction.status === 'completed'
                          ? '#10b981'
                          : transaction.status === 'pending'
                            ? '#f59e0b'
                            : '#ef4444',
                    }}
                  />
                </Box>
              </Box>
            </Zoom>
          ))}
          {(!transactions || transactions.length === 0) && (
            <Box textAlign="center" py={4}>
              <HistoryIcon sx={{ fontSize: 48, color: darkMode ? '#475569' : '#cbd5e1', mb: 2 }} />
              <Typography variant="body2" color="textSecondary">No transactions yet</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderTransactions = () => (
    <Card sx={{
      background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
      borderRadius: { xs: 12, sm: 16 },
    }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{
              background: 'rgba(99, 102, 241, 0.15)',
              width: 40,
              height: 40,
            }}>
              <HistoryIcon sx={{ color: '#6366f1', fontSize: 20 }} />
            </Avatar>
            <Typography variant="h6" fontWeight="600">All Transactions</Typography>
          </Box>
          <IconButton onClick={fetchUserData} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          {transactions?.map((transaction, index) => (
            <Zoom in={true} style={{ transitionDelay: `${index * 30}ms` }} key={transaction.id}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderRadius: { xs: 8, sm: 12 },
                background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                transition: 'all 0.2s',
                '&:hover': {
                  background: darkMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.05)',
                },
              }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{
                    background: transaction.transactionType === 'deposit' ? 'rgba(16, 185, 129, 0.15)' :
                                   transaction.transactionType === 'withdrawal' ? 'rgba(239, 68, 68, 0.15)' :
                                   'rgba(139, 92, 246, 0.15)',
                    width: 36,
                    height: 36,
                  }}>
                    {transaction.transactionType === 'deposit' ? <WalletIcon sx={{ color: '#10b981', fontSize: 18 }} /> :
                     transaction.transactionType === 'withdrawal' ? <WithdrawIcon sx={{ color: '#ef4444', fontSize: 18 }} /> :
                     <SwapHorizIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                      {transaction.transactionType ?
                        transaction.transactionType.charAt(0).toUpperCase() + transaction.transactionType.slice(1) :
                        'Unknown'}
                      {transaction.recipientUsername && ` to ${transaction.recipientUsername}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      {new Date(transaction.createdAt).toLocaleString()} • {transaction.description || 'No description'}
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body1" fontWeight="700" sx={{ fontSize: '0.9rem' }}>
                    {transaction.transactionType === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount, transaction.currency)}
                  </Typography>
                  <Chip
                    label={transaction.status}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      borderRadius: 4,
                      background:
                        transaction.status === 'completed'
                          ? 'rgba(16, 185, 129, 0.15)'
                          : transaction.status === 'pending'
                            ? 'rgba(245, 158, 11, 0.15)'
                            : 'rgba(239, 68, 68, 0.15)',
                      color:
                        transaction.status === 'completed'
                          ? '#10b981'
                          : transaction.status === 'pending'
                            ? '#f59e0b'
                            : '#ef4444',
                    }}
                  />
                </Box>
              </Box>
            </Zoom>
          ))}
          {(!transactions || transactions.length === 0) && (
            <Box textAlign="center" py={4}>
              <HistoryIcon sx={{ fontSize: 48, color: darkMode ? '#475569' : '#cbd5e1', mb: 2 }} />
              <Typography variant="body2" color="textSecondary">No transactions yet</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderProfile = () => (
    <Card sx={{
      background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
      borderRadius: { xs: 12, sm: 16 },
    }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            width: 56,
            height: 56,
          }}>
            {user?.fullName?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600">{user?.fullName || 'User'}</Typography>
            <Typography variant="caption" color="textSecondary">{user?.email}</Typography>
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, borderRadius: { xs: 8, sm: 12 }, background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>Username</Typography>
              <Typography variant="body1" fontWeight="600">{user?.username}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, borderRadius: { xs: 8, sm: 12 }, background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>Default Currency</Typography>
              <Typography variant="body1" fontWeight="600">{user?.defaultCurrency || 'EUR'}</Typography>
            </Box>
          </Grid>
        </Grid>
        <Button
          variant="contained"
          fullWidth
          onClick={() => setProfileDialogOpen(true)}
          sx={{ mt: 3, py: 1.5, borderRadius: { xs: 8, sm: 12 }, fontWeight: 600 }}
        >
          Edit Profile
        </Button>
      </CardContent>
    </Card>
  );

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    searchUsers(usernameInput);
  }, [usernameInput]);

  // Render drawer
  const drawer = () => (
    <Box>
      <Toolbar sx={{
        background: darkMode
          ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
        minHeight: { xs: 56, sm: 64 },
      }}>
        <Typography variant="h6" noWrap component="div" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          User Panel
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)' }} />
      <List sx={{ pt: { xs: 1, sm: 2 } }}>
        {menuItems.map((item, index) => (
          <ListItem
            button
            key={item.id}
            selected={tabValue === index}
            onClick={() => {
              setTabValue(index);
              setMobileMenuOpen(false);
            }}
            sx={{
              borderRadius: 0,
              mb: 0.5,
              py: 1.25,
              transition: 'all 0.2s',
              '&.Mui-selected': {
                background: darkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.1)',
              },
              '&:hover': {
                background: darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{
              color: tabValue === index ? '#6366f1' : (darkMode ? '#94a3b8' : '#64748b'),
              minWidth: { xs: 40, sm: 36 },
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', sm: '0.9rem' },
                  color: tabValue === index ? '#6366f1' : (darkMode ? '#94a3b8' : '#64748b'),
                },
              }}
            />
          </ListItem>
        ))}
        <ListItem
          button
          onClick={() => {
            setApplyDialogOpen(true);
            setMobileMenuOpen(false);
          }}
          sx={{
            borderRadius: { xs: 8, sm: 12 },
            mb: 0.5,
            py: 1.25,
            transition: 'all 0.2s',
            '&:hover': {
              background: darkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{
            color: '#8b5cf6',
            minWidth: { xs: 40, sm: 36 },
          }}>
            <FundingIcon />
          </ListItemIcon>
          <ListItemText
            primary="Apply for Funding"
            sx={{
              '& .MuiTypography-root': {
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '0.9rem' },
                color: '#8b5cf6',
              },
            }}
          />
        </ListItem>
      </List>
      <Divider sx={{ my: 2 }} />
      <List sx={{ px: 2 }}>
        <ListItem
          button
          onClick={() => handleLogout()}
          sx={{
            borderRadius: { xs: 8, sm: 12 },
            py: 1.25,
            transition: 'all 0.2s',
            '&:hover': {
              background: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: '#ef4444', minWidth: { xs: 40, sm: 36 } }}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            sx={{
              '& .MuiTypography-root': {
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '0.9rem' },
                color: '#ef4444',
              },
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  // Show loading state while initializing or while data is being fetched
  // Only show loading if we're still initializing or actively loading data
  const isDataLoading = isInitializing || isLoading;
  
  if (isDataLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: darkMode ? '#0f172a' : '#f8fafc' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
          <Typography variant="body2" color="textSecondary" fontWeight="500">Loading your dashboard...</Typography>
        </Box>
      </Box>
    );
  }

  // Show error if there's one
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: darkMode ? '#0f172a' : '#f8fafc' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400, p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => window.location.href = '/login'}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  // Create dynamic theme based on dark mode
  const dynamicTheme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#6366f1',
        light: '#818cf8',
        dark: '#4f46e5',
      },
      secondary: {
        main: '#8b5cf6',
        light: '#a78bfa',
        dark: '#7c3aed',
      },
      error: {
        main: '#ef4444',
      },
      warning: {
        main: '#f59e0b',
      },
      success: {
        main: '#10b981',
      },
      background: {
        default: darkMode ? '#0f172a' : '#f8fafc',
        paper: darkMode ? '#1e293b' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#f1f5f9' : '#0f172a',
        secondary: darkMode ? '#94a3b8' : '#64748b',
      },
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      body1: {
        fontSize: '0.95rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 0,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            boxShadow: darkMode
              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 2px 16px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: darkMode
                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 8px 28px rgba(0, 0, 0, 0.1)',
            },
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(20px) saturate(180%)',
            background: darkMode
              ? 'rgba(15, 23, 42, 0.85)'
              : 'rgba(255, 255, 255, 0.85)',
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
            background: darkMode
              ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.99) 100%)'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.99) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: darkMode
              ? '4px 0 24px rgba(0, 0, 0, 0.3)'
              : '4px 0 24px rgba(0, 0, 0, 0.1)',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
          },
          contained: {
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderWidth: 1.5,
          },
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            fontWeight: 500,
            fontSize: '0.75rem',
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
            },
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            boxShadow: darkMode
              ? '0 24px 80px rgba(0, 0, 0, 0.5)'
              : '0 24px 80px rgba(0, 0, 0, 0.15)',
          }
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: 64,
            background: darkMode
              ? 'rgba(15, 23, 42, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
          }
        }
      },
    }
  });

  return (
    <ThemeProvider theme={dynamicTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', background: darkMode ? '#0f172a' : '#f8fafc', minHeight: '100vh' }}>
          {/* App Bar - Desktop */}
          {!isMobile && (
            <AppBar
              position="fixed"
              sx={{
                width: `calc(100% - ${isSmallMobile ? 0 : 260}px)`,
                ml: isSmallMobile ? 0 : '260px',
                bgcolor: 'transparent',
                color: dynamicTheme.palette.text.primary,
                boxShadow: 'none',
                height: 64,
                zIndex: 1100,
              }}
            >
              <Toolbar>
                <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                  {`Welcome, ${user?.fullName || 'User'}`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    color="inherit"
                    onClick={() => fetchUserData()}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 10,
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    onClick={() => setDarkMode(!darkMode)}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 10,
                    }}
                  >
                    {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                  <Avatar sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    width: 40,
                    height: 40,
                  }}>
                    {user?.fullName?.charAt(0) || 'U'}
                  </Avatar>
                </Box>
              </Toolbar>
            </AppBar>
          )}

          {/* Sidebar - Desktop */}
          {!isMobile && (
            <Box
              component="nav"
              sx={{ width: 260, flexShrink: 0 }}
            >
              <Drawer
                variant="permanent"
                open
                sx={{
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: 260,
                    bgcolor: dynamicTheme.palette.background.paper,
                    borderRight: 'none',
                    overflow: 'hidden',
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 0,
                    background: darkMode
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                  }}>
                      <Avatar sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        width: 44,
                        height: 44,
                      }}>
                        {user?.fullName?.charAt(0) || 'U'}
                      </Avatar>
                      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant="body1" fontWeight="600" noWrap sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user?.fullName || 'User'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" noWrap sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user?.email}
                        </Typography>
                      </Box>
                  </Box>
                <List sx={{ px: 2 }}>
                  {menuItems.map((item, index) => (
                    <ListItem
                      button
                      key={item.id}
                      selected={tabValue === index}
                      onClick={() => setTabValue(index)}
                      sx={{
                        borderRadius: { xs: 8, sm: 12 },
                        mb: 0.5,
                        py: 1.25,
                        transition: 'all 0.2s',
                        '&.Mui-selected': {
                          background: darkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.1)',
                        },
                        '&:hover': {
                          background: darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{
                        color: tabValue === index ? '#6366f1' : (darkMode ? '#94a3b8' : '#64748b'),
                        minWidth: { xs: 40, sm: 36 },
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          '& .MuiTypography-root': {
                            fontWeight: 600,
                            fontSize: { xs: '0.95rem', sm: '0.9rem' },
                            color: tabValue === index ? '#6366f1' : (darkMode ? '#94a3b8' : '#64748b'),
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                  <ListItem
                    button
                    onClick={() => setApplyDialogOpen(true)}
                    sx={{
                      borderRadius: { xs: 8, sm: 12 },
                      mb: 0.5,
                      py: 1.25,
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: darkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: '#8b5cf6',
                      minWidth: { xs: 40, sm: 36 },
                    }}>
                      <FundingIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Apply for Funding"
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          fontSize: { xs: '0.95rem', sm: '0.9rem' },
                          color: '#8b5cf6',
                        },
                      }}
                    />
                  </ListItem>
                </List>
                </Box>
                <Box sx={{ mt: 'auto', p: 2 }}>
                  <ListItem
                    button
                    onClick={handleLogout}
                    sx={{
                      borderRadius: { xs: 8, sm: 12 },
                      py: 1.25,
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: '#ef4444', minWidth: { xs: 40, sm: 36 } }}>
                      <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Logout"
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          fontSize: { xs: '0.95rem', sm: '0.9rem' },
                          color: '#ef4444',
                        },
                      }}
                    />
                  </ListItem>
                </Box>
              </Drawer>
            </Box>
          )}

          {/* Mobile Header */}
          {isMobile && (
            <AppBar
              position="fixed"
              sx={{
                bgcolor: 'transparent',
                color: dynamicTheme.palette.text.primary,
                boxShadow: 'none',
                height: 56,
                zIndex: 1100,
              }}
            >
              <Toolbar sx={{ minHeight: 56 }}>
                <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1rem' }}>
                  {menuItems[tabValue]?.label || 'Dashboard'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => fetchUserData()}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 8,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setDarkMode(!darkMode)}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 8,
                    }}
                  >
                    {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setMobileMenuOpen(true)}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 8,
                    }}
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Toolbar>
            </AppBar>
          )}

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: 280,
                bgcolor: dynamicTheme.palette.background.paper,
              },
            }}
          >
            {drawer()}
          </Drawer>

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: isMobile ? '100%' : 'calc(100% - 260px)',
              mt: isMobile ? 7 : 8,
              bgcolor: dynamicTheme.palette.background.default,
              minHeight: '100vh',
              pb: isMobile ? 8 : 0,
            }}
          >
            {/* Alerts */}
            <Slide direction="down" in={!!error || !!success}>
              <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
                {success && (
                  <Alert
                    severity="success"
                    sx={{
                      mb: 2,
                      borderRadius: 12,
                      background: darkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.08)',
                      color: '#10b981',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      '& .MuiAlert-icon': {
                        color: '#10b981',
                      },
                    }}
                    onClose={() => setSuccess(null)}
                  >
                    {success}
                  </Alert>
                )}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2,
                      borderRadius: 12,
                      background: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                      '& .MuiAlert-icon': {
                        color: '#ef4444',
                      },
                    }}
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                )}
              </Box>
            </Slide>
            {/* Main content for each menu tab */}
            <Box sx={{ px: { xs: 2, md: 3 }, mt: 2 }}>
              {tabValue === 0 && (
                <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {error ? (
                        <Alert severity="error" onClose={() => setError(null)}>
                          {error}
                        </Alert>
                      ) : (
                        <>
                          {renderDashboardStats()}
                        </>
                      )}
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <Box display="flex" flexDirection="column" gap={3}>
                        {renderBalanceTrends()}
                        {renderRecentTransactions()}
                      </Box>
                    </Grid>
                  </Grid>
                </Container>
              )}

              {tabValue === 1 && (
                <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
                  {renderTransactions()}
                </Container>
              )}

              {tabValue === 2 && (
                <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
                    Transfer Funds
                  </Typography>
                  <Card sx={{ p: 3, borderRadius: { xs: 12, sm: 16 } }}>
                    {/* Transfer Type Selection */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Select Transfer Method
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip
                          icon={<AccountBalanceIcon />}
                          label="Username"
                          clickable
                          color={transferType === 'username' ? 'primary' : 'default'}
                          onClick={() => setTransferType('username')}
                          sx={{ px: 2, py: 2, fontSize: '1rem' }}
                        />
                        <Chip
                          icon={<AccountBalanceIcon />}
                          label="Account"
                          clickable
                          color={transferType === 'account' ? 'primary' : 'default'}
                          onClick={() => setTransferType('account')}
                          sx={{ px: 2, py: 2, fontSize: '1rem' }}
                        />
                        <Chip
                          icon={<CreditCardIcon />}
                          label="Card"
                          clickable
                          color={transferType === 'card' ? 'primary' : 'default'}
                          onClick={() => setTransferType('card')}
                          sx={{ px: 2, py: 2, fontSize: '1rem' }}
                        />
                      </Box>
                    </Box>

                    <Box component="form" onSubmit={handleTransfer}>
                      {transferType === 'username' && (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Autocomplete
                              freeSolo
                              options={userOptions}
                              getOptionLabel={(option) => option.username || option}
                              onInputChange={(e, value) => setUsernameInput(value)}
                              onChange={(e, value) => {
                                if (typeof value === 'object') {
                                  setTransferForm({ ...transferForm, recipientUsername: value.username });
                                }
                              }}
                              loading={searchLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  label="Recipient Username"
                                  placeholder="Enter username"
                                  value={transferForm.recipientUsername}
                                  onChange={(e) => setTransferForm({ ...transferForm, recipientUsername: e.target.value })}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Currency</InputLabel>
                              <Select
                                value={transferForm.currency}
                                label="Currency"
                                onChange={(e) => setTransferForm({ ...transferForm, currency: e.target.value as 'EUR' | 'USD' })}
                              >
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Amount"
                              type="number"
                              value={transferForm.amount}
                              onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description"
                              multiline
                              rows={2}
                              value={transferForm.description}
                              onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              fullWidth
                              sx={{ borderRadius: { xs: 8, sm: 12 } }}
                            >
                              Send Transfer
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      {transferType === 'account' && (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Beneficiary"
                              value={accountTransferForm.beneficiary}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, beneficiary: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Account Number"
                              value={accountTransferForm.accountNumber}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, accountNumber: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Bank Name"
                              value={accountTransferForm.bankName}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, bankName: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="SWIFT/BIC"
                              value={accountTransferForm.swiftBic}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, swiftBic: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Country"
                              value={accountTransferForm.country}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, country: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              value={accountTransferForm.phoneNumber}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, phoneNumber: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Address"
                              multiline
                              rows={2}
                              value={accountTransferForm.address}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, address: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Currency</InputLabel>
                              <Select
                                value={accountTransferForm.currency}
                                label="Currency"
                                onChange={(e) => setAccountTransferForm({ ...accountTransferForm, currency: e.target.value as 'EUR' | 'USD' })}
                              >
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Amount"
                              type="number"
                              value={accountTransferForm.amount}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, amount: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description"
                              multiline
                              rows={2}
                              value={accountTransferForm.description}
                              onChange={(e) => setAccountTransferForm({ ...accountTransferForm, description: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              fullWidth
                              sx={{ borderRadius: { xs: 8, sm: 12 } }}
                            >
                              Send Transfer
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      {transferType === 'card' && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Card Type</InputLabel>
                              <Select
                                value={cardTransferForm.cardType}
                                label="Card Type"
                                onChange={(e) => setCardTransferForm({ ...cardTransferForm, cardType: e.target.value as 'VISA' | 'MASTER' })}
                              >
                                <MenuItem value="VISA">VISA</MenuItem>
                                <MenuItem value="MASTER">MASTER</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Currency</InputLabel>
                              <Select
                                value={cardTransferForm.currency}
                                label="Currency"
                                onChange={(e) => setCardTransferForm({ ...cardTransferForm, currency: e.target.value as 'EUR' | 'USD' })}
                              >
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Card Number"
                              value={cardTransferForm.cardNumber}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, cardNumber: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Cardholder Name"
                              value={cardTransferForm.cardholderName}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, cardholderName: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Amount"
                              type="number"
                              value={cardTransferForm.amount}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, amount: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Expiry Month"
                              value={cardTransferForm.expiryMonth}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, expiryMonth: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Expiry Year"
                              value={cardTransferForm.expiryYear}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, expiryYear: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="CVV"
                              value={cardTransferForm.cvv}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, cvv: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description"
                              multiline
                              rows={2}
                              value={cardTransferForm.description}
                              onChange={(e) => setCardTransferForm({ ...cardTransferForm, description: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              fullWidth
                              sx={{ borderRadius: { xs: 8, sm: 12 } }}
                            >
                              Send Transfer
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Card>
                </Container>
              )}

              {tabValue === 3 && (
                <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
                    Withdraw Funds
                  </Typography>
                  <Card sx={{ p: 3, borderRadius: { xs: 12, sm: 16 } }}>
                    {/* Withdrawal Method Selection */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Select Withdrawal Method
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip
                          icon={<CreditCardIcon />}
                          label="Card"
                          clickable
                          color={withdrawMethod === 'card' ? 'primary' : 'default'}
                          onClick={() => setWithdrawMethod('card')}
                          sx={{ px: 2, py: 2, fontSize: '1rem' }}
                        />
                        <Chip
                          icon={<AccountBalanceIcon />}
                          label="Bank"
                          clickable
                          color={withdrawMethod === 'bank' ? 'primary' : 'default'}
                          onClick={() => setWithdrawMethod('bank')}
                          sx={{ px: 2, py: 2, fontSize: '1rem' }}
                        />
                      </Box>
                    </Box>

                    <Box component="form" onSubmit={handleWithdraw}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Currency</InputLabel>
                            <Select
                              value={withdrawForm.currency}
                              label="Currency"
                              onChange={(e) => setWithdrawForm({ ...withdrawForm, currency: e.target.value as 'EUR' | 'USD' })}
                            >
                              <MenuItem value="EUR">EUR</MenuItem>
                              <MenuItem value="USD">USD</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {withdrawMethod === 'card' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Card Type</InputLabel>
                                <Select
                                  value={withdrawForm.cardType}
                                  label="Card Type"
                                  onChange={(e) => setWithdrawForm({ ...withdrawForm, cardType: e.target.value as 'VISA' | 'MASTER' })}
                                >
                                  <MenuItem value="VISA">VISA</MenuItem>
                                  <MenuItem value="MASTER">MASTER</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {creditCards && creditCards.length > 0 && (
                              <Grid item xs={12}>
                                <FormControl fullWidth>
                                  <InputLabel>Use Saved Card (optional)</InputLabel>
                                  <Select
                                    value={withdrawForm.creditCardId}
                                    label="Use Saved Card (optional)"
                                    onChange={(e) => setWithdrawForm({ ...withdrawForm, creditCardId: e.target.value })}
                                  >
                                    <MenuItem value="">Enter new card</MenuItem>
                                    {creditCards.map((card) => (
                                      <MenuItem key={card.id} value={card.id.toString()}>
                                        {card.cardType} ending in {card.cardLastFour}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Card Number"
                                value={withdrawForm.cardNumber}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, cardNumber: e.target.value })}
                                required={!withdrawForm.creditCardId}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Cardholder Name"
                                value={withdrawForm.cardholderName}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, cardholderName: e.target.value })}
                                required={!withdrawForm.creditCardId}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Expiry Month"
                                value={withdrawForm.expiryMonth}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, expiryMonth: e.target.value })}
                                required={!withdrawForm.creditCardId}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Expiry Year"
                                value={withdrawForm.expiryYear}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, expiryYear: e.target.value })}
                                required={!withdrawForm.creditCardId}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="CVV"
                                value={withdrawForm.cvv}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, cvv: e.target.value })}
                                required={!withdrawForm.creditCardId}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Amount"
                                type="number"
                                value={withdrawForm.amount}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                                required
                              />
                            </Grid>
                          </>
                        )}

                        {withdrawMethod === 'bank' && (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Beneficiary"
                                value={withdrawForm.beneficiary}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, beneficiary: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Account Number"
                                value={withdrawForm.accountNumber}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, accountNumber: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Bank Name"
                                value={withdrawForm.bankName}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, bankName: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="SWIFT/BIC"
                                value={withdrawForm.swiftBic}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, swiftBic: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Country"
                                value={withdrawForm.country}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, country: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Phone Number"
                                value={withdrawForm.phoneNumber}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, phoneNumber: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Address"
                                multiline
                                rows={2}
                                value={withdrawForm.address}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, address: e.target.value })}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Amount"
                                type="number"
                                value={withdrawForm.amount}
                                onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                                required
                              />
                            </Grid>
                          </>
                        )}

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={2}
                            value={withdrawForm.description}
                            onChange={(e) => setWithdrawForm({ ...withdrawForm, description: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{ borderRadius: { xs: 8, sm: 12 } }}
                          >
                            Withdraw Funds
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Container>
              )}

              {tabValue === 4 && (
                <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
                  {renderProfile()}
                </Container>
              )}
            </Box>

            {/* Profile Dialog */}
            <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: { xs: 12, sm: 16 } } }}>
              <DialogTitle>Edit Profile</DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      placeholder="Enter your full name"
                      value={profileForm.fullName}
                      onChange={(e) => setProfileForm({ ...profileForm, fullName: e.target.value })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      placeholder="Enter your email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      placeholder="Enter your phone number"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>Address</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      placeholder="Street address"
                      value={profileForm.address.street}
                      onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, street: e.target.value } })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      placeholder="City"
                      value={profileForm.address.city}
                      onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, city: e.target.value } })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State/Province"
                      placeholder="State or province"
                      value={profileForm.address.state}
                      onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, state: e.target.value } })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      placeholder="ZIP or postal code"
                      value={profileForm.address.zipCode}
                      onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, zipCode: e.target.value } })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      placeholder="Country"
                      value={profileForm.address.country}
                      onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, country: e.target.value } })}
                      sx={{ mb: 2.5 }}
                      InputProps={{
                        sx: { borderRadius: { xs: 8, sm: 12 } },
                      }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <FormControl fullWidth sx={{ mt: 2.5 }}>
                <InputLabel>Default Currency</InputLabel>
                <Select
                  value={profileForm.defaultCurrency}
                  label="Default Currency"
                  onChange={(e) => setProfileForm({ ...profileForm, defaultCurrency: e.target.value as 'EUR' | 'USD' })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: { xs: 8, sm: 12 },
                    },
                  }}
                >
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
              <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                  onClick={() => setProfileDialogOpen(false)}
                  sx={{ borderRadius: { xs: 8, sm: 12 }, fontWeight: 500 }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateProfile}
                  variant="contained"
                  sx={{ borderRadius: { xs: 8, sm: 12 }, fontWeight: 600 }}
                >
                  Update Profile
                </Button>
              </DialogActions>
            </Dialog>

            {/* Apply modal (trigger removed) */}
            <ApplyForFundingModal open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} />

            {/* Bottom Navigation - Mobile */}
            {isMobile && (
              <BottomNavigation
                value={tabValue}
                onChange={(_, newValue) => {
                  setTabValue(newValue);
                }}
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1200,
                  height: 56,
                  '& .MuiBottomNavigationAction-root': {
                    minWidth: 'auto',
                    padding: '6px 12px',
                    fontSize: '0.7rem',
                  },
                }}
              >
                <BottomNavigationAction label="Dashboard" icon={<DashboardIcon />} />
                <BottomNavigationAction label="Transactions" icon={<HistoryIcon />} />
                <BottomNavigationAction label="Transfer" icon={<SwapHorizIcon />} />
                <BottomNavigationAction label="Withdraw" icon={<WithdrawIcon />} />
                <BottomNavigationAction label="Profile" icon={<AccountCircleIcon />} />
              </BottomNavigation>
            )}
          </Box>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

// Add display name for better debugging in React DevTools
UserDashboardModern.displayName = 'UserDashboardModern';

// Export the component
// Note: For future performance optimization, consider wrapping with React.memo()
// if the component receives props and needs to prevent unnecessary re-renders
export default UserDashboardModern;

